import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Modal,
  Button,
  Text,
  Stack,
  TextInput,
  Group,
  Loader,
  Box,
  Tabs,
  ActionIcon,
  Tooltip,
  SegmentedControl,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import {
  IconLink,
  IconLock,
  IconCode,
  IconCopy,
  IconX,
  IconTrash,
} from '@tabler/icons-react';
import { websiteDomain, clientRoutes } from '@/ApiPaths';
import { copyToClipboard } from '@/services/Utility';
import type {
  LinkShareMode,
  ShareLinkDuration,
  ProductShareTokenMeta,
} from '@/types/Product.model';
import type { IProduct } from '@/types/Product.model';
import {
  getProductForEdit,
  updateProductLinkShare,
  createProductShareToken,
  listProductShareTokens,
  revokeProductShareToken,
} from '@/Api';
import { isApiError } from '@/services/httpClient';
import { isPermissionError } from '@/utils/errorHandling';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { ProductEmbedPanel } from '@/components/ProductShare/ProductEmbedPanel';
import { LinkAccessControl } from '@/components/ProductShare/LinkAccessControl';
import { ProductQRCode } from '@/components/ProductShare/ProductQRCode';
import classes from './ProductShareModal.module.css';

export type ProductShareTab = 'link' | 'access' | 'embed';

export interface ProductShareModalProps {
  opened: boolean;
  onClose: () => void;
  productId: string;
  productName?: string;
  linkShareMode?: LinkShareMode;
  isOwner?: boolean;
  initialTab?: ProductShareTab;
  accentColor?: string;
  qrLogoUrl?: string;
  onLinkSaved?: (patch: Partial<IProduct>) => void;
  showEmbed?: boolean;
}

function accentButtonStyles(accentColor: string) {
  return {
    root: {
      backgroundColor: accentColor,
      color: '#fff',
      '&:hover': { filter: 'brightness(0.93)' },
    },
  } as const;
}

export function ProductShareModal({
  opened,
  onClose,
  productId,
  productName,
  linkShareMode: linkShareModeProp = 'open',
  isOwner = false,
  initialTab = 'link',
  accentColor = 'var(--mantine-color-gray-7)',
  qrLogoUrl,
  onLinkSaved,
  showEmbed,
}: ProductShareModalProps) {
  const { t } = useTranslation();
  const embedEnabled = showEmbed ?? isOwner;
  const [activeTab, setActiveTab] = useState<ProductShareTab>(initialTab);

  const [loadingProduct, setLoadingProduct] = useState(false);
  const [fullProduct, setFullProduct] = useState<IProduct | null>(null);
  const [localMode, setLocalMode] = useState<LinkShareMode>('open');
  const [localPassword, setLocalPassword] = useState('');
  const [updatePassword, setUpdatePassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [shareDuration, setShareDuration] = useState<ShareLinkDuration>('7d');
  const [shareTokens, setShareTokens] = useState<ProductShareTokenMeta[]>([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);

  const effectiveMode = isOwner ? localMode : linkShareModeProp;
  const passwordAlreadySet = Boolean(fullProduct?.linkSharePasswordSet);
  const displayName = isOwner ? fullProduct?.productName ?? productName : productName;

  const productUrl = useMemo(() => {
    const path = clientRoutes.product.replace(':id', productId);
    return `${websiteDomain}${path}`;
  }, [productId]);

  const temporaryUrlForToken = useCallback(
    (rawToken: string) => `${productUrl}?share=${encodeURIComponent(rawToken)}`,
    [productUrl]
  );

  const activeShareTokens = useMemo(() => {
    const now = Date.now();
    return shareTokens.filter(
      (row) => !row.revoked && new Date(row.expiresAt).getTime() > now
    );
  }, [shareTokens]);

  const durationLabel = useCallback(
    (d: ShareLinkDuration) => {
      if (d === '1d') return t('dashboard.shareModal.duration1d');
      if (d === '30d') return t('dashboard.shareModal.duration30d');
      return t('dashboard.shareModal.duration7d');
    },
    [t]
  );

  const refreshShareTokens = useCallback(async () => {
    if (!productId || !isOwner) return;
    setLoadingTokens(true);
    try {
      const tokens = await listProductShareTokens(productId);
      setShareTokens(tokens);
    } catch {
      /* list errors are non-blocking while modal stays open */
    } finally {
      setLoadingTokens(false);
    }
  }, [productId, isOwner]);

  const visibleTabs = useMemo(() => {
    const tabs: ProductShareTab[] = ['link'];
    if (isOwner) tabs.push('access');
    if (isOwner && embedEnabled) tabs.push('embed');
    return tabs;
  }, [isOwner, embedEnabled]);

  const shellStyle = useMemo(
    () =>
      ({
        '--share-accent': accentColor,
      }) as CSSProperties,
    [accentColor]
  );

  useEffect(() => {
    if (!opened) return;
    setActiveTab(visibleTabs.includes(initialTab) ? initialTab : visibleTabs[0] ?? 'link');
  }, [opened, initialTab, visibleTabs]);

  useEffect(() => {
    if (!opened || !isOwner || !productId) return;
    let cancelled = false;
    setLoadingProduct(true);
    setFullProduct(null);
    setLocalPassword('');
    setUpdatePassword(false);
    getProductForEdit(productId)
      .then((res) => {
        if (cancelled) return;
        setFullProduct(res);
        setLocalMode((res.linkShareMode as LinkShareMode) ?? 'open');
        setLoadingProduct(false);
      })
      .catch((error) => {
        if (cancelled) return;
        if (isPermissionError(error)) {
          notifications.show({
            title: t('dashboard.notifications.error'),
            message: t('dashboard.shareModal.notAuthorized'),
            color: 'red',
          });
          onClose();
        } else {
          notifications.show({
            title: t('dashboard.notifications.error'),
            message: t('dashboard.shareModal.loadError'),
            color: 'red',
          });
        }
        setLoadingProduct(false);
      });
    void refreshShareTokens();
    return () => {
      cancelled = true;
    };
  }, [opened, isOwner, productId, onClose, t, refreshShareTokens]);

  const handleCopy = async (url = productUrl) => {
    try {
      await copyToClipboard(url);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.notifications.linkCopied'),
        color: 'green',
      });
    } catch {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.notifications.copyFailed'),
        color: 'red',
      });
    }
  };

  const handleCreateTemporaryLink = async () => {
    if (!productId) return;
    setCreatingToken(true);
    try {
      await createProductShareToken(productId, shareDuration);
      await refreshShareTokens();
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.shareModal.temporaryLinkCreated'),
        color: 'green',
      });
    } catch {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.shareModal.createLinkError'),
        color: 'red',
      });
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (tokenId: string) => {
    if (!productId) return;
    setRevokingTokenId(tokenId);
    try {
      await revokeProductShareToken(productId, tokenId);
      setShareTokens((prev) =>
        prev.map((row) =>
          row._id === tokenId ? { ...row, revoked: true } : row
        )
      );
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.shareModal.linkRevoked'),
        color: 'green',
      });
    } catch {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.shareModal.revokeError'),
        color: 'red',
      });
    } finally {
      setRevokingTokenId(null);
    }
  };

  const handleSaveLinkSettings = async () => {
    if (!fullProduct?._id) return;

    if (localMode === 'password') {
      const mustEnterPassword = !passwordAlreadySet || updatePassword;
      if (mustEnterPassword && localPassword.trim().length < 4) {
        notifications.show({
          title: t('editProduct.system'),
          message: t('editProduct.linkShare.passwordTooShort'),
          color: 'red',
        });
        return;
      }
    }

    setSaving(true);
    try {
      const sendPassword =
        localMode === 'password' && (!passwordAlreadySet || updatePassword);

      const saved = await updateProductLinkShare(fullProduct._id, {
        linkShareMode: localMode,
        ...(sendPassword ? { linkSharePassword: localPassword.trim() } : {}),
      });
      setFullProduct(saved);
      setLocalMode((saved.linkShareMode as LinkShareMode) ?? 'open');
      setLocalPassword('');
      setUpdatePassword(false);
      onLinkSaved?.({
        linkShareMode: saved.linkShareMode,
        linkSharePasswordSet: saved.linkSharePasswordSet,
        isPublic: saved.isPublic,
      });
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.shareModal.saved'),
        color: 'green',
      });
    } catch (e: unknown) {
      let msg = t('dashboard.shareModal.saveError');
      if (isApiError(e) && e.response.data && typeof e.response.data === 'object') {
        const d = e.response.data as { message?: string };
        if (typeof d.message === 'string') msg = d.message;
      }
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: msg,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const guestAccessHint =
    effectiveMode === 'password'
      ? t('productViewer.linkShare.passwordModeHint')
      : effectiveMode === 'restricted'
        ? t('productViewer.linkShare.restrictedModeHint')
        : t('productViewer.linkShare.openModeHint');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      title={false}
      centered
      size="lg"
      radius="lg"
      padding="lg"
      overlayProps={{ blur: 3 }}
      styles={{
        content: {
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.14)',
          maxWidth: 640,
        },
        header: {
          display: 'none',
        },
        body: {
          paddingTop: 'var(--mantine-spacing-md)',
        },
      }}
    >
      <div className={classes.shell} style={shellStyle}>
        <header className={classes.header}>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text className={classes.title}>{t('dashboard.shareModal.title')}</Text>
              {displayName ? (
                <Text className={classes.subtitle} lineClamp={2}>
                  {displayName}
                </Text>
              ) : null}
            </Box>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              radius="md"
              aria-label={t('dashboard.actions.close')}
              onClick={onClose}
            >
              <IconX size={18} />
            </ActionIcon>
          </Group>
        </header>

        {isOwner && loadingProduct ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(v) => setActiveTab((v as ProductShareTab) ?? 'link')}
              variant="unstyled"
            >
              <Tabs.List className={classes.tabList}>
                <Tabs.Tab value="link" className={classes.tab} leftSection={<IconLink size={15} />}>
                  {t('dashboard.shareModal.tabs.link')}
                </Tabs.Tab>
                {isOwner ? (
                  <Tabs.Tab value="access" className={classes.tab} leftSection={<IconLock size={15} />}>
                    {t('dashboard.shareModal.tabs.access')}
                  </Tabs.Tab>
                ) : null}
                {isOwner && embedEnabled ? (
                  <Tabs.Tab value="embed" className={classes.tab} leftSection={<IconCode size={15} />}>
                    {t('dashboard.shareModal.tabs.embed')}
                  </Tabs.Tab>
                ) : null}
              </Tabs.List>

              <Tabs.Panel value="link" className={classes.panel}>
                <div className={classes.linkRow}>
                  <div className={classes.linkMain}>
                    <div className={classes.card}>
                      <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={10}>
                        {t('dashboard.shareModal.urlSection')}
                      </Text>
                      <div className={classes.urlField}>
                        <TextInput
                          className={classes.urlInput}
                          value={productUrl}
                          readOnly
                          size="sm"
                          styles={{ input: { background: 'var(--mantine-color-body)' } }}
                        />
                        <Tooltip label={t('dashboard.actions.copy')}>
                          <ActionIcon
                            variant="light"
                            size="lg"
                            radius="md"
                            aria-label={t('dashboard.actions.copy')}
                            onClick={() => void handleCopy()}
                            style={{
                              backgroundColor: colorWithAlpha(accentColor, 0.1),
                              color: accentColor,
                            }}
                          >
                            <IconCopy size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </div>

                      {!isOwner ? (
                        <Text className={classes.hintBanner}>{guestAccessHint}</Text>
                      ) : (
                        <Text size="xs" c="dimmed" mt={10} style={{ lineHeight: 1.45 }}>
                          {t('dashboard.shareModal.linkHelp')}
                        </Text>
                      )}
                    </div>

                    {isOwner ? (
                      <div className={classes.card} style={{ marginTop: 16 }}>
                        <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={8}>
                          {t('dashboard.shareModal.temporaryLinkSection')}
                        </Text>
                        <Text size="xs" c="dimmed" mb={12} style={{ lineHeight: 1.45 }}>
                          {t('dashboard.shareModal.temporaryLinkHelp')}
                        </Text>
                        <Text size="xs" fw={500} mb={6}>
                          {t('dashboard.shareModal.durationLabel')}
                        </Text>
                        <SegmentedControl
                          fullWidth
                          value={shareDuration}
                          onChange={(v) => setShareDuration(v as ShareLinkDuration)}
                          data={[
                            { label: t('dashboard.shareModal.duration1d'), value: '1d' },
                            { label: t('dashboard.shareModal.duration7d'), value: '7d' },
                            { label: t('dashboard.shareModal.duration30d'), value: '30d' },
                          ]}
                          mb="sm"
                        />
                        <Button
                          fullWidth
                          variant="light"
                          loading={creatingToken}
                          onClick={() => void handleCreateTemporaryLink()}
                          style={{
                            color: accentColor,
                            borderColor: colorWithAlpha(accentColor, 0.35),
                          }}
                        >
                          {t('dashboard.shareModal.createTemporaryLink')}
                        </Button>

                        <Text size="xs" fw={600} tt="uppercase" c="dimmed" mt={16} mb={8}>
                          {t('dashboard.shareModal.activeTemporaryLinks')}
                        </Text>
                        {loadingTokens ? (
                          <Group justify="center" py="sm">
                            <Loader size="xs" />
                          </Group>
                        ) : activeShareTokens.length === 0 ? (
                          <Text size="xs" c="dimmed">
                            {t('dashboard.shareModal.noActiveTemporaryLinks')}
                          </Text>
                        ) : (
                          <Stack gap={10}>
                            {activeShareTokens.map((row) => {
                              const linkUrl = row.token
                                ? temporaryUrlForToken(row.token)
                                : '';
                              return (
                                <Box
                                  key={row._id}
                                  style={{
                                    padding: '10px 12px',
                                    borderRadius: 8,
                                    background: 'var(--mantine-color-body)',
                                    border: '1px solid var(--mantine-color-default-border)',
                                  }}
                                >
                                  <Group justify="space-between" wrap="nowrap" gap="xs" mb={linkUrl ? 8 : 0}>
                                    <Box style={{ minWidth: 0, flex: 1 }}>
                                      <Text size="sm" fw={500}>
                                        {durationLabel(row.duration)}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        {t('dashboard.shareModal.expiresAt', {
                                          date: new Date(row.expiresAt).toLocaleString(),
                                        })}
                                      </Text>
                                    </Box>
                                    <Tooltip label={t('dashboard.shareModal.revokeLink')}>
                                      <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size="md"
                                        loading={revokingTokenId === row._id}
                                        aria-label={t('dashboard.shareModal.revokeLink')}
                                        onClick={() => void handleRevokeToken(row._id)}
                                      >
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Group>
                                  {linkUrl ? (
                                    <div className={classes.urlField}>
                                      <TextInput
                                        className={classes.urlInput}
                                        value={linkUrl}
                                        readOnly
                                        size="xs"
                                        styles={{ input: { background: 'var(--mantine-color-body)' } }}
                                      />
                                      <Tooltip label={t('dashboard.shareModal.copyTemporaryLink')}>
                                        <ActionIcon
                                          variant="light"
                                          size="md"
                                          radius="md"
                                          aria-label={t('dashboard.shareModal.copyTemporaryLink')}
                                          onClick={() => void handleCopy(linkUrl)}
                                          style={{
                                            backgroundColor: colorWithAlpha(accentColor, 0.1),
                                            color: accentColor,
                                          }}
                                        >
                                          <IconCopy size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </div>
                                  ) : null}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className={classes.linkQr}>
                    <div className={classes.qrCard}>
                      <Text size="xs" fw={600} c="dimmed" mb={8} ta="center">
                        {t('dashboard.shareModal.qrSection')}
                      </Text>
                      <ProductQRCode
                        value={productUrl}
                        accentColor={accentColor}
                        logoImage={qrLogoUrl}
                        showStyleToggle
                        variant="inline"
                        size={168}
                      />
                    </div>
                  </div>
                </div>
              </Tabs.Panel>

              {isOwner ? (
                <Tabs.Panel value="access" className={classes.panel}>
                  <div className={classes.card}>
                    <LinkAccessControl
                      accentColor={accentColor}
                      linkShareMode={localMode}
                      onLinkShareModeChange={setLocalMode}
                      sectionHelp={t('editProduct.linkShare.sectionHelp')}
                      passwordAlreadySet={passwordAlreadySet}
                      updatePassword={updatePassword}
                      onUpdatePasswordChange={setUpdatePassword}
                      password={localPassword}
                      onPasswordChange={setLocalPassword}
                    />
                  </div>
                </Tabs.Panel>
              ) : null}

              {isOwner && embedEnabled ? (
                <Tabs.Panel value="embed" className={`${classes.panel} ${classes.panelScroll}`}>
                  <ProductEmbedPanel productId={productId} accentColor={accentColor} compact />
                </Tabs.Panel>
              ) : null}
            </Tabs>

            <footer className={classes.footer}>
              <Button variant="subtle" color="gray" onClick={onClose}>
                {t('dashboard.actions.close')}
              </Button>
              <div className={classes.footerActions}>
                {activeTab === 'link' ? (
                  <Button
                    leftSection={<IconCopy size={16} />}
                    styles={accentButtonStyles(accentColor)}
                    onClick={() => void handleCopy()}
                  >
                    {t('dashboard.actions.copy')}
                  </Button>
                ) : null}
                {isOwner && activeTab === 'access' ? (
                  <Button
                    loading={saving}
                    disabled={loadingProduct || !fullProduct}
                    styles={accentButtonStyles(accentColor)}
                    onClick={() => void handleSaveLinkSettings()}
                  >
                    {t('dashboard.shareModal.save')}
                  </Button>
                ) : null}
              </div>
            </footer>
          </>
        )}
      </div>
    </Modal>
  );
}
